"use client";
import { BriefcaseBusiness, ArrowRight } from "lucide-react";
import Image from "next/image";
import { blogs } from "../../lib/blogs";
import Link from "next/link";

const Blogs = ({ category }) => {
    // Create efficient blog lookup to avoid filtering large array
    const filteredBlogs = category
        ? blogs.filter((blog) => blog.category === category)
        : blogs.slice(0, 3);

    const sortedBlogs = filteredBlogs.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
    );
    return (
        <section
            id="latest-insights"
            className='w-full py-12 px-4 sm:px-6 lg:py-20 lg:px-12 bg-gray-50/30'
            aria-labelledby="blog-heading"
        >
            <div className='max-w-7xl mx-auto flex flex-col items-center gap-10 lg:gap-16'>

                {/* Heading Section */}
                <header className="text-center flex flex-col items-center gap-4 w-full">
                    <span className="inline-flex items-center gap-2 border-2 border-brand text-brand px-4 py-2 rounded-full text-xs font-bold shadow-sm w-fit uppercase tracking-wider">
                        <BriefcaseBusiness className="w-4 h-4" aria-hidden="true" />
                        Key Insights
                    </span>
                    <h2 id="blog-heading" className='max-w-4xl text-3xl md:text-5xl lg:text-6xl font-light text-gray-900 leading-tight'>
                        Insights for <span className="text-brand font-semibold">Smarter Decisions</span>
                    </h2>
                </header>

                {/* Blog Grid */}
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
                    {sortedBlogs.map((blog, index) => (
                        <article
                            key={blog.slug || index}
                            className={`group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col border border-gray-100 overflow-hidden 
                                ${index === 2 ? 'hidden lg:flex' : 'flex'}`}
                        >
                            {/* Image Link */}
                            <Link
                                href={`/blog/${blog.slug}`}
                                className="relative aspect-[16/9] w-full overflow-hidden block"
                                aria-label={`Read more about ${blog.title}`}
                            >
                                <Image
                                    src={blog.image}
                                    alt={`Cover image showing ${blog.title} topic`}
                                    fill
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                    className="object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                            </Link>

                            <div className="flex flex-col flex-1 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    {/* ISO format in dateTime attribute for SEO */}
                                    {/*<time
                                        dateTime={new Date(blog.date).toISOString().split('T')[0]}
                                        className="text-xs text-gray-500 font-medium"
                                    >
                                        {blog.date}
                                    </time>*/}
                                    <span className="bg-brand/10 text-brand text-[10px] px-3 py-1 rounded-full font-bold uppercase">
                                        {blog.readingTime}
                                    </span>
                                </div>

                                <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-brand transition-colors line-clamp-2">
                                    <Link href={`/blog/${blog.slug}`} className="hover:underline focus:outline-brand">
                                        {blog.title}
                                    </Link>
                                </h3>

                                <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-6">
                                    {blog.description}
                                </p>

                                <div className="mt-auto">
                                    <Link
                                        href={`/blog/${blog.slug}`}
                                        className="inline-flex w-full h-11 items-center justify-center gap-2 bg-brand hover:bg-[#3d5f99] text-white rounded-md font-semibold transition-all focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                                    >
                                        Read Full Article
                                        <span className="sr-only"> regarding {blog.title}</span>
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="pt-4">
                    <Link
                        href="/blog"
                        className="text-brand font-semibold hover:underline text-lg transition-all focus-visible:ring-2 focus-visible:ring-brand rounded-md px-2 py-1"
                    >
                        View all insights
                    </Link>
                </div>
            </div>
        </section>
    );
}

export default Blogs;
